// SOS Game Logic

// Check for SOS patterns formed by the last move
export const checkSOSPatterns = (board, size, row, col) => {
  const patterns = [];
  const directions = [
    { dr: 0, dc: 1, name: 'horizontal' },    // horizontal
    { dr: 1, dc: 0, name: 'vertical' },      // vertical
    { dr: 1, dc: 1, name: 'diagonal-down' }, // diagonal \
    { dr: 1, dc: -1, name: 'diagonal-up' },  // diagonal /
  ];

  // Check all possible SOS patterns that include position (row, col)
  for (const { dr, dc } of directions) {
    // Check 3 positions: current cell could be S(start), O(middle), or S(end)
    
    // Pattern 1: Current cell is first S
    if (isValidSOS(board, size, row, col, row + dr, col + dc, row + 2*dr, col + 2*dc)) {
      patterns.push([
        [row, col],
        [row + dr, col + dc],
        [row + 2*dr, col + 2*dc]
      ]);
    }
    
    // Pattern 2: Current cell is middle O
    if (isValidSOS(board, size, row - dr, col - dc, row, col, row + dr, col + dc)) {
      patterns.push([
        [row - dr, col - dc],
        [row, col],
        [row + dr, col + dc]
      ]);
    }
    
    // Pattern 3: Current cell is last S
    if (isValidSOS(board, size, row - 2*dr, col - 2*dc, row - dr, col - dc, row, col)) {
      patterns.push([
        [row - 2*dr, col - 2*dc],
        [row - dr, col - dc],
        [row, col]
      ]);
    }
  }

  return patterns;
};

// Helper to check if three positions form valid SOS
const isValidSOS = (board, size, r1, c1, r2, c2, r3, c3) => {
  // Check bounds
  if (r1 < 0 || r1 >= size || c1 < 0 || c1 >= size) return false;
  if (r2 < 0 || r2 >= size || c2 < 0 || c2 >= size) return false;
  if (r3 < 0 || r3 >= size || c3 < 0 || c3 >= size) return false;
  
  // Check SOS pattern
  return board[r1][c1] === 'S' && board[r2][c2] === 'O' && board[r3][c3] === 'S';
};

// Check if game is over (board is full)
export const isSOSGameOver = (board, size) => {
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!board[r][c]) return false;
    }
  }
  return true;
};

// Get all SOS patterns on the board (for highlighting)
export const getAllSOSPatterns = (board, size) => {
  const patterns = [];
  const seen = new Set();

  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (board[row][col]) {
        const cellPatterns = checkSOSPatterns(board, size, row, col);
        for (const pattern of cellPatterns) {
          const key = pattern.map(p => `${p[0]},${p[1]}`).sort().join('|');
          if (!seen.has(key)) {
            seen.add(key);
            patterns.push(pattern);
          }
        }
      }
    }
  }

  return patterns;
};

// AI for SOS game
export const getSOSAIMove = (board, size, difficulty) => {
  const emptyCells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!board[r][c]) {
        emptyCells.push([r, c]);
      }
    }
  }

  if (emptyCells.length === 0) return null;

  // Easy: Random move with random letter
  if (difficulty === 'easy') {
    const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const letter = Math.random() > 0.5 ? 'S' : 'O';
    return { row, col, letter };
  }

  // For medium/hard/expert: Try to form SOS or block opponent
  
  // First, try to complete an SOS
  for (const [row, col] of emptyCells) {
    for (const letter of ['S', 'O']) {
      const testBoard = board.map(r => [...r]);
      testBoard[row][col] = letter;
      const patterns = checkSOSPatterns(testBoard, size, row, col);
      if (patterns.length > 0) {
        return { row, col, letter };
      }
    }
  }

  // For hard/expert: Look ahead to avoid giving opponent SOS opportunity
  if (difficulty === 'hard' || difficulty === 'expert') {
    // Find moves that don't set up opponent
    const safeMoves = [];
    for (const [row, col] of emptyCells) {
      for (const letter of ['S', 'O']) {
        const testBoard = board.map(r => [...r]);
        testBoard[row][col] = letter;
        
        // Check if this gives opponent an SOS opportunity
        let givesSOS = false;
        for (let r = 0; r < size && !givesSOS; r++) {
          for (let c = 0; c < size && !givesSOS; c++) {
            if (!testBoard[r][c]) {
              for (const l of ['S', 'O']) {
                const testBoard2 = testBoard.map(row => [...row]);
                testBoard2[r][c] = l;
                if (checkSOSPatterns(testBoard2, size, r, c).length > 0) {
                  givesSOS = true;
                  break;
                }
              }
            }
          }
        }
        
        if (!givesSOS) {
          safeMoves.push({ row, col, letter });
        }
      }
    }
    
    if (safeMoves.length > 0) {
      return safeMoves[Math.floor(Math.random() * safeMoves.length)];
    }
  }

  // Default: Random move
  const [row, col] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
  const letter = Math.random() > 0.5 ? 'S' : 'O';
  return { row, col, letter };
};

// Create empty SOS board
export const createEmptySOSBoard = (size) => {
  return Array(size).fill(null).map(() => Array(size).fill(null));
};
